import {getProfileReviewQueueState} from './profileReviewQueueState';
import ProfileReviewQueueStatus from './ProfileReviewQueueStatus';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import LogFormShell from '../../components/UI/LogFormShell';
import { useRecentCareSources } from './useRecentCareSources';
import {
  getCareSourceTitle,
} from './careSourceStatusModel';
import { getPersonIdentityProfileSuggestions } from './aiReviewPolicy';
import {
  getDirectProfileReviewItems,
} from './DirectProfileOrganizationReview';
import ProfileCareSourceReviewGroup from './ProfileCareSourceReviewGroup';
import LegacyProfileSuggestionReview from './LegacyProfileSuggestionReview';
import {
  buildManualReviewQueueSummary,
  buildManualReviewSourceSummary,
  formatDestinationCount,
  groupManualReviewSourcesByCapture,
  sortDestinationEntries,
} from './profileReviewQueueModel';
import { getProfileSourceReviewSuggestions } from './profileSourceReviewSummary';
import { useManualReviewQueueModel } from './useManualReviewQueueModel';
import { fetchMedications } from '../../services/medicationService';

export const PROFILE_REVIEW_REQUEST_EVENT = 'captureez:profile-review-request';

const getDirectPendingItems = (source = {}) => {
  const decisions = source.directProfileReviewDecisions || {};
  return getDirectProfileReviewItems(source, getProfileSourceReviewSuggestions(source))
    .filter((item) => !decisions[item.clientId]);
};

const getLegacyPendingCount = (source = {}) => {
  const decisions = source.directProfileReviewDecisions || {};
  return getPersonIdentityProfileSuggestions(source).reduce((count, group) => (
    count + group.items.filter((_, index) => !decisions[`legacy-profile:${group.key}:${index}`]).length
  ), 0);
};

export const getPendingProfileSuggestionCount = (source = {}) => {
  return buildManualReviewSourceSummary(source).pendingTotal;
};

export const isPendingProfileSuggestionSource = (source = {}) => (
  Boolean(source.id) && getPendingProfileSuggestionCount(source) > 0
);

const getSourceIcon = (source = {}) => {
  const type = String(
    source.sourceType
    || source.sourceKind
    || source.sourceMetadata?.sourceType
    || source.metadata?.source?.sourceType
    || ''
  ).toLowerCase();
  if (/photo|image|camera|screenshot/.test(type)) return '📷';
  if (/voice|audio|dictat/.test(type)) return '🎤';
  if (/email/.test(type)) return '✉️';
  if (/document|file|pdf/.test(type)) return '📄';
  return '📝';
};

const ProfileReviewQueue = ({
  childId = '',
  personName = 'this person',
  dialogOnly = false,
  notifyTimelineChanges = true,
  canResolve = true,
}) => {
  const [open, setOpen] = useState(false);
  const [requestedSourceIds, setRequestedSourceIds] = useState(null);
  const [existingMedications, setExistingMedications] = useState([]);
  const { error, sources, loading, reload } = useRecentCareSources({
    childId,
    includeAllMatching: true,
    activityFilter: isPendingProfileSuggestionSource,
    notifyTimelineChanges,
  });
  const manualReview = useManualReviewQueueModel(sources);
  useEffect(() => {
    let active = true;
    if (!childId) {
      setExistingMedications([]);
      return undefined;
    }
    fetchMedications(childId)
      .then((medications) => {
        if (active) setExistingMedications(medications || []);
      })
      .catch(() => {
        if (active) setExistingMedications([]);
      });
    return () => {
      active = false;
    };
  }, [childId]);
  const pendingSources = useMemo(
    () => sources.filter((source) => manualReview.pendingSourceIds.has(source.id)),
    [sources, manualReview.pendingSourceIds]
  );
  const displayedPendingSources = useMemo(() => {
    if (!requestedSourceIds) return pendingSources;
    const requestedIds = new Set(requestedSourceIds);
    return pendingSources.filter((source) => requestedIds.has(source.id));
  }, [pendingSources, requestedSourceIds]);
  const reviewSummary = useMemo(() => (
    requestedSourceIds
      ? buildManualReviewQueueSummary(
        displayedPendingSources,
        manualReview.structuredDestinationsBySource
      )
      : manualReview.summary
  ), [
    displayedPendingSources,
    manualReview.structuredDestinationsBySource,
    manualReview.summary,
    requestedSourceIds,
  ]);
  const sourceGroups = useMemo(
    () => groupManualReviewSourcesByCapture(displayedPendingSources),
    [displayedPendingSources]
  );
  const destinationEntries = useMemo(
    () => sortDestinationEntries(reviewSummary.destinations),
    [reviewSummary.destinations]
  );
  const jumpToDestination = (label) => {
    if (typeof document === 'undefined') return;
    const destination = [...document.querySelectorAll('[data-review-destination]')]
      .find((node) => node.dataset.reviewDestination === label);
    destination?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    destination?.querySelector('button')?.focus?.();
  };

  useEffect(() => {
    if (canResolve && typeof window !== 'undefined' && window.location.hash === '#profile-review') {
      const requestedSourceId = new URLSearchParams(window.location.search).get('reviewSource') || '';
      setRequestedSourceIds(requestedSourceId ? [requestedSourceId] : null);
      setOpen(true);
    }
  }, [canResolve, childId]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleReviewRequest = (event) => {
      const requestedChildId = event?.detail?.childId || '';
      if (requestedChildId && requestedChildId !== childId) return;
      setRequestedSourceIds(Array.isArray(event?.detail?.sourceIds)
        ? event.detail.sourceIds.filter(Boolean)
        : null);
      setOpen(true);
    };

    window.addEventListener(PROFILE_REVIEW_REQUEST_EVENT, handleReviewRequest);
    return () => window.removeEventListener(PROFILE_REVIEW_REQUEST_EVENT, handleReviewRequest);
  }, [childId]);

  if (!pendingSources.length && !open) return null;
  const reviewState = getProfileReviewQueueState({
    loading, error: error || manualReview.receiptError,
    receiptsReady: manualReview.receiptsReady,
    scoped: Boolean(requestedSourceIds), pendingCount: reviewSummary.pendingTotal,
  });
  const reviewComplete = reviewState.status === 'complete';
  const retryReview = () => { reload?.(); manualReview.retryReceipts(); };
  const viewFullQueue = () => {
    setRequestedSourceIds(null);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('reviewSource');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
  };
  const closeReview = () => {
    setOpen(false);
    setRequestedSourceIds(null);
  };

  return (
    <>
      {!dialogOnly && !reviewComplete ? (
        <Paper
          variant="outlined"
          sx={{
            px: 1.2,
            py: 1.1,
            borderRadius: '16px',
            borderColor: alpha(colors.semantic.sageDeep, 0.3),
            bgcolor: alpha(colors.semantic.sageMist, 0.72),
            boxShadow: 'none',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Box>
              <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: colors.semantic.ink }}>
                Needs your review
              </Typography>
              <Typography sx={{ mt: 0.2, fontSize: '0.82rem', color: colors.semantic.ink2 }}>
                {destinationEntries
                  .map(([label, count]) => formatDestinationCount(label, count))
                  .join(' · ')}
              </Typography>
            </Box>
            {canResolve ? (
              <Button
                variant="contained"
                onClick={() => setOpen(true)}
                sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, textTransform: 'none', fontWeight: 500, boxShadow: 'none' }}
              >
                Review
              </Button>
            ) : (
              <Typography sx={{ fontSize: '0.8rem', color: colors.semantic.ink2 }}>
                Awaiting a Care Owner or Care Partner
              </Typography>
            )}
          </Stack>
        </Paper>
      ) : null}

      <LogFormShell
        open={open}
        onClose={closeReview}
        title={reviewState.title}
        subtitle={reviewState.subtitle}
        forceDrawer
        compactTitle
        surfaceSx={{ height: '100dvh', maxHeight: '100dvh' }}
        headerSx={{ py: 1.5, px: { xs: 2, sm: 3 } }}
        bodySx={{ px: { xs: 1.25, sm: 3 }, pt: 1.25, pb: 3 }}
        headerContent={reviewState.status !== 'pending' ? null : (
          <Stack spacing={0.8}>
            <Stack component="ul" direction="row" useFlexGap flexWrap="wrap" spacing={0.5} sx={{ m: 0, p: 0, listStyle: 'none' }}>
              {destinationEntries.map(([label, count]) => (
                <Box component="li" key={label}>
                  <Button
                    size="small"
                    onClick={() => jumpToDestination(label)}
                    sx={{
                      minWidth: 0,
                      px: 0.85,
                      py: 0.25,
                      borderRadius: 999,
                      bgcolor: alpha(colors.semantic.sageMist, 0.72),
                      fontSize: '0.74rem',
                      color: colors.semantic.ink2,
                      textTransform: 'none',
                    }}
                  >
                    {formatDestinationCount(label, count)}
                  </Button>
                </Box>
              ))}
            </Stack>
          </Stack>
        )}
      >
        <Stack spacing={1.15}>
          <ProfileReviewQueueStatus state={reviewState} scoped={Boolean(requestedSourceIds)} totalPending={manualReview.summary.pendingTotal} onViewAll={viewFullQueue} onRetry={retryReview}/>
          {reviewState.status === 'pending' ? <>
            {sourceGroups.map((sourceGroup) => {
              const representativeSource = sourceGroup.sources[0];
              return (
                <Box key={sourceGroup.key}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.65, px: 0.25 }}>
                    <Typography aria-hidden="true">{getSourceIcon(representativeSource)}</Typography>
                    <Box>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: colors.semantic.ink2 }}>
                        {representativeSource.title
                          || representativeSource.sourceTitle
                          || getCareSourceTitle(representativeSource)
                          || 'Care note'}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack spacing={1}>
                    {sourceGroup.sources.map((source) => {
                      const directCount = getDirectPendingItems(source).length;
                      const structuredCount = (manualReview.candidatesBySource[source.id] || []).length;
                      const personCount = getLegacyPendingCount(source);
                      return (
                        <React.Fragment key={source.id}>
                          {directCount || structuredCount ? (
                            <ProfileCareSourceReviewGroup
                              source={source}
                              personName={personName}
                              onVisibleCandidatesChange={(result) => (
                                manualReview.updateVisibleCandidates(source.id, result)
                              )}
                              visibleStructuredCandidates={manualReview.candidatesBySource[source.id] || []}
                              existingMedications={existingMedications}
                            />
                          ) : null}
                          {personCount ? <LegacyProfileSuggestionReview source={source} /> : null}
                        </React.Fragment>
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}
          </> : null}
        </Stack>
      </LogFormShell>
    </>
  );
};

export default ProfileReviewQueue;
